/*
 * /schedule (page)
 */
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import interactionPlugin from "@fullcalendar/interaction"
import type { DateClickArg } from "@fullcalendar/interaction"
import type { EventClickArg, EventInput } from "@fullcalendar/core"
import {
	Button,
	cn,
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Form,
	InputFormField,
	Label,
	PageHeader,
	PageWithEffect,
	Popover,
	PopoverContent,
	PopoverTrigger,
	SelectFormField,
	TextareaFormField,
	toast,
} from "@emr/react-ui"
import type { Practitioner, Patient } from "@emr/db/server"
import { createFileRoute } from "@tanstack/react-router"
import { Check, ChevronsUpDown, Globe, Loader2, Plus } from "lucide-react"
import { useMemo, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"
import { z } from "zod"
import { trpc } from "../trpc"
import { rolePageLoader } from "../utils/auth.utils"
import { useAuth } from "@emr/auth/client"
import { isAdmin } from "@emr/utils"

export const Route = createFileRoute("/schedule")({
	component: SchedulePage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

/* ─── Constants ──────────────────────────────────────────────────────────── */

const DAYS_OF_WEEK = [
	{ label: "Sun", value: 0 },
	{ label: "Mon", value: 1 },
	{ label: "Tue", value: 2 },
	{ label: "Wed", value: 3 },
	{ label: "Thu", value: 4 },
	{ label: "Fri", value: 5 },
	{ label: "Sat", value: 6 },
] as const

const APPOINTMENT_TYPES = [
	{ value: "initial", label: "Initial Consultation" },
	{ value: "follow_up", label: "Follow-Up" },
	{ value: "procedure", label: "Procedure" },
	{ value: "phone", label: "Phone Call" },
	{ value: "other", label: "Other" },
]

const APPOINTMENT_STATUSES = [
	{ value: "scheduled", label: "Scheduled" },
	{ value: "confirmed", label: "Confirmed" },
	{ value: "cancelled", label: "Cancelled" },
	{ value: "completed", label: "Completed" },
	{ value: "no_show", label: "No Show" },
]

const STATUS_COLORS: Record<string, string> = {
	scheduled: "#3b82f6",
	confirmed: "#10b981",
	cancelled: "#ef4444",
	completed: "#6b7280",
	no_show: "#f59e0b",
}

const ALL_TIMEZONES: string[] =
	typeof Intl !== "undefined" && "supportedValuesOf" in Intl
		? (Intl as any).supportedValuesOf("timeZone")
		: []

/**
 * Convert a UTC Date/string to a local Date whose clock values match
 * the given IANA timezone. Passed to FullCalendar with timeZone="local"
 * so it displays the correct time regardless of the browser's system TZ.
 */
function utcToDisplayDate(utcInput: Date | string, tz: string): Date {
	const utc = new Date(utcInput as any)
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	}).formatToParts(utc)
	const get = (type: string) =>
		parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10)
	const h = get("hour")
	return new Date(
		get("year"),
		get("month") - 1,
		get("day"),
		h % 24,
		get("minute"),
		get("second"),
	)
}

/** Extract YYYY-MM-DD and HH:MM from a UTC date in a given IANA timezone. */
function utcToLocalParts(utcDate: Date | string, tz: string): { date: string; time: string } {
	const d = new Date(utcDate as any)
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).formatToParts(d)
	const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ""
	const h = parseInt(get("hour"), 10) % 24
	return {
		date: `${get("year")}-${get("month")}-${get("day")}`,
		time: `${String(h).padStart(2, "0")}:${get("minute")}`,
	}
}

/** Convert a local date + time string in a timezone to a UTC Date (DST-correct). */
function clientLocalToUTC(dateStr: string, timeStr: string, tz: string): Date {
	const naive = new Date(`${dateStr}T${timeStr}:00.000Z`)
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone: tz,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hour12: false,
	}).formatToParts(naive)
	const get = (t: string) =>
		parseInt(parts.find((p) => p.type === t)?.value ?? "0", 10)
	const tzAsUTC = Date.UTC(
		get("year"),
		get("month") - 1,
		get("day"),
		get("hour") % 24,
		get("minute"),
		get("second"),
	)
	return new Date(naive.getTime() + (naive.getTime() - tzAsUTC))
}

/* ─── Schemas ────────────────────────────────────────────────────────────── */

const rangeSchema = z.object({
	practitionerId: z.string().min(1, "Required"),
	startDate: z.string().min(1, "Required"),
	endDate: z.string().min(1, "Required"),
	startTime: z.string().min(1, "Required"),
	endTime: z.string().min(1, "Required"),
	timezone: z.string().min(1, "Required"),
	notes: z.string().optional(),
})

const appointmentSchema = z.object({
	patientId: z.string().optional(),
	type: z.string().optional(),
	status: z.string().default("scheduled"),
	reason: z.string().optional(),
	notes: z.string().optional(),
	startTime: z.string().min(1, "Required"),
	endTime: z.string().min(1, "Required"),
})

type RangeValues = z.infer<typeof rangeSchema>
type AppointmentValues = z.infer<typeof appointmentSchema>

/* ─── Timezone helpers ───────────────────────────────────────────────────── */

/**
 * Searchable timezone combobox that lives inside a react-hook-form Form context.
 */
function TimezoneFormField({ name, label }: { name: string; label: string }) {
	const { setValue, watch } = useFormContext()
	const value = (watch(name) as string) || ""
	const [open, setOpen] = useState(false)

	return (
		<div className={"flex flex-col gap-1.5"}>
			<Label className={"text-sm font-medium"}>{label}</Label>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						type={"button"}
						variant={"outline"}
						role={"combobox"}
						className={"w-full justify-between font-normal"}
					>
						<span className={"truncate"}>{value || "Select timezone…"}</span>
						<ChevronsUpDown className={"ml-2 size-4 shrink-0 opacity-50"} />
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className={"w-[var(--radix-popover-trigger-width)] p-0"}
					align={"start"}
				>
					<Command>
						<CommandInput placeholder={"Search timezone…"} />
						<CommandList className={"max-h-52"}>
							<CommandEmpty>No timezone found.</CommandEmpty>
							<CommandGroup>
								{ALL_TIMEZONES.map((tz) => (
									<CommandItem
										key={tz}
										value={tz}
										onSelect={() => {
											setValue(name, tz, { shouldDirty: true })
											setOpen(false)
										}}
									>
										<Check
											className={cn(
												"mr-2 size-4",
												value === tz ? "opacity-100" : "opacity-0",
											)}
										/>
										{tz}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	)
}

/**
 * Standalone timezone picker popover (for the page header — no form context needed).
 */
function TimezonePopover({
	value,
	onSelect,
	isPending,
}: {
	value: string
	onSelect: (tz: string) => void
	isPending?: boolean
}) {
	const [open, setOpen] = useState(false)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type={"button"}
					variant={"ghost"}
					size={"sm"}
					className={"h-auto gap-1 px-2 py-1 text-sm font-medium"}
					disabled={isPending}
				>
					<Globe className={"size-3.5"} />
					{value}
					<ChevronsUpDown className={"size-3 opacity-50"} />
				</Button>
			</PopoverTrigger>
			<PopoverContent className={"w-72 p-0"} align={"start"}>
				<Command>
					<CommandInput placeholder={"Search timezone…"} />
					<CommandList className={"max-h-52"}>
						<CommandEmpty>No timezone found.</CommandEmpty>
						<CommandGroup>
							{ALL_TIMEZONES.map((tz) => (
								<CommandItem
									key={tz}
									value={tz}
									onSelect={() => {
										onSelect(tz)
										setOpen(false)
									}}
								>
									<Check
										className={cn(
											"mr-2 size-4",
											value === tz ? "opacity-100" : "opacity-0",
										)}
									/>
									{tz}
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

function SchedulePage() {
	const { user } = useAuth()
	const userIsAdmin = isAdmin(user)
	const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone

	const calendarRef = useRef<FullCalendar>(null)

	const [createAvailOpen, setCreateAvailOpen] = useState(false)
	const [newApptSlot, setNewApptSlot] = useState<{
		availabilityId: string
		practitionerId: string
		startsAt: Date
		endsAt: Date
		timezone: string
		defaultAppointmentMinutes: number | null
	} | null>(null)

	// User's own record (for stored timezone preference)
	const { data: myUser, refetch: refetchMyUser } =
		trpc.user.getByFilter.useQuery(
			{ where: { id: user?.id ?? "" } },
			{ enabled: !!user?.id },
		)

	const updateMyTimezone = trpc.user.updateMyTimezone.useMutation({
		onSuccess: () => {
			refetchMyUser()
			toast.success("Timezone updated")
		},
		onError: (e) => toast.error(e.message),
	})

	// The timezone used to display the calendar — prefer the user's stored preference
	const displayTZ = myUser?.timezone ?? browserTZ

	// Data queries
	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery(
		{ where: { active: true } },
	)

	const { data: availabilities = [], refetch: refetchAvail } =
		trpc.practitionerAvailability.getAllByFilter.useQuery({
			where: { active: true },
		})

	const { data: appts = [], refetch: refetchAppts } =
		trpc.appointment.getAllByFilter.useQuery({
			where: { active: true },
		})

	const { data: patients = [] } = trpc.patient.getAllByFilter.useQuery({
		where: { active: true },
	})

	// Build FullCalendar events — pre-convert UTC → display timezone so
	// FullCalendar can use timeZone="local" reliably on all browsers.
	const events = useMemo<EventInput[]>(() => {
		const availEvents: EventInput[] = (availabilities ?? []).map((a) => ({
			id: `avail-${a.id}`,
			start: utcToDisplayDate(a.startsAt, displayTZ),
			end: utcToDisplayDate(a.endsAt, displayTZ),
			backgroundColor: "#e0f2fe",
			borderColor: "#0ea5e9",
			textColor: "#0369a1",
			title: (() => {
				const prac = (practitioners ?? []).find((p) => p.id === a.practitionerId)
				return prac ? `${prac.firstName} ${prac.lastName}` : "Available"
			})(),
			extendedProps: { type: "availability", availability: a },
		}))

		const apptEvents: EventInput[] = (appts ?? []).map((ap) => ({
			id: `appt-${ap.id}`,
			start: utcToDisplayDate(ap.startsAt, displayTZ),
			end: utcToDisplayDate(ap.endsAt, displayTZ),
			backgroundColor: STATUS_COLORS[ap.status] ?? "#6b7280",
			borderColor: STATUS_COLORS[ap.status] ?? "#6b7280",
			textColor: "#ffffff",
			title: (() => {
				const prac = (practitioners ?? []).find((p) => p.id === ap.practitionerId)
				const pat = (patients ?? []).find((p) => p.id === ap.patientId)
				const pracName = prac ? `${prac.firstName} ${prac.lastName}` : ""
				const patName = pat ? `${pat.firstName} ${pat.lastName}` : "No patient"
				return `${pracName} — ${patName}`
			})(),
			extendedProps: { type: "appointment", appointment: ap },
		}))

		return [...availEvents, ...apptEvents]
	}, [availabilities, appts, practitioners, patients, displayTZ])

	function handleEventClick(info: EventClickArg) {
		const { type, availability } = info.event.extendedProps
		if (type === "availability" && userIsAdmin) {
			const prac = (practitioners ?? []).find((p) => p.id === availability.practitionerId)
			setNewApptSlot({
				availabilityId: availability.id,
				practitionerId: availability.practitionerId,
				startsAt: new Date(availability.startsAt),
				endsAt: new Date(availability.endsAt),
				timezone: availability.timezone,
				defaultAppointmentMinutes: prac?.defaultAppointmentMinutes ?? null,
			})
		}
	}

	function handleDateClick(info: DateClickArg) {
		const api = calendarRef.current?.getApi()
		if (api && api.view.type !== "timeGridDay") {
			api.changeView("timeGridDay", info.date)
		}
	}

	return (
		<PageWithEffect effect={"aurora"} centerVertical={false}>
			<Container width={"xxl"} padding={"lg"} gap={"lg"}>
				<PageHeader
					title={"Schedule"}
					right={{
						Component: (
							<Button size={"sm"} onClick={() => setCreateAvailOpen(true)}>
								<Plus className={"mr-1.5 size-4"} />
								Add Availability
							</Button>
						),
					}}
				/>

				{/* Timezone + Legend row */}
				<div className={"flex flex-wrap items-center gap-4"}>
					<div className={"flex items-center gap-1 text-sm text-muted-foreground"}>
						<span>Showing in:</span>
						<TimezonePopover
							value={displayTZ}
							isPending={updateMyTimezone.isPending}
							onSelect={(tz) => updateMyTimezone.mutate({ timezone: tz })}
						/>
					</div>

					<div className={"flex flex-wrap gap-4 text-xs text-muted-foreground"}>
						<span className={"flex items-center gap-1.5"}>
							<span
								className={
									"inline-block size-3 rounded-sm border border-sky-400 bg-sky-200"
								}
							/>
							Availability
						</span>
						{Object.entries(STATUS_COLORS).map(([status, color]) => (
							<span key={status} className={"flex items-center gap-1.5"}>
								<span
									className={"inline-block size-3 rounded-sm"}
									style={{ backgroundColor: color }}
								/>
								{status.replace("_", " ")}
							</span>
						))}
					</div>
				</div>

				{/* Calendar */}
				<div className={"rounded-lg border bg-background p-2"}>
					<FullCalendar
						key={displayTZ}
						ref={calendarRef}
						plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
						initialView={"timeGridWeek"}
						headerToolbar={{
							left: "prev,next today",
							center: "title",
							right: "dayGridMonth,timeGridWeek,timeGridDay",
						}}
						timeZone={"local"}
						slotMinTime={"06:00:00"}
						slotMaxTime={"22:00:00"}
						allDaySlot={false}
						nowIndicator
						height={"auto"}
						events={events}
						eventClick={handleEventClick}
						dateClick={handleDateClick}
						eventContent={(info) => (
							<div className={"overflow-hidden px-1 text-xs leading-snug"}>
								<p className={"truncate font-medium"}>{info.event.title}</p>
								<p className={"truncate opacity-80"}>{info.timeText}</p>
							</div>
						)}
					/>
				</div>
			</Container>

			{/* Create Availability */}
			<CreateAvailabilityDialog
				open={createAvailOpen}
				onOpenChange={setCreateAvailOpen}
				practitioners={practitioners ?? []}
				defaultTimezone={displayTZ}
				onSuccess={() => {
					refetchAvail()
					setCreateAvailOpen(false)
				}}
			/>

			{/* Book Appointment (admin only) */}
			{userIsAdmin && newApptSlot && (
				<BookAppointmentDialog
					slot={newApptSlot}
					patients={patients ?? []}
					open={!!newApptSlot}
					onOpenChange={(o) => !o && setNewApptSlot(null)}
					onSuccess={() => {
						refetchAppts()
						setNewApptSlot(null)
					}}
				/>
			)}
		</PageWithEffect>
	)
}

/* ─── Create Availability Dialog ─────────────────────────────────────────── */

function CreateAvailabilityDialog({
	open,
	onOpenChange,
	practitioners,
	defaultTimezone,
	onSuccess,
}: {
	open: boolean
	onOpenChange: (o: boolean) => void
	practitioners: Practitioner[]
	defaultTimezone: string
	onSuccess: () => void
}) {
	const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5])

	const createRange = trpc.practitionerAvailability.createRange.useMutation({
		onError: (e) => toast.error(e.message),
	})

	const practitionerOptions = practitioners.map((p) => ({
		value: p.id,
		label: `${p.firstName} ${p.lastName}`,
	}))

	// Stable reference — prevents the Form's defaultValues effect from
	// resetting field values every time selectedDays state changes.
	const formDefaults = useMemo<RangeValues>(
		() => ({
			practitionerId: practitioners[0]?.id ?? "",
			startDate: "",
			endDate: "",
			startTime: "09:00",
			endTime: "17:00",
			timezone: defaultTimezone,
			notes: "",
		}),
		 
		[practitioners[0]?.id, defaultTimezone],
	)

	function toggleDay(day: number) {
		setSelectedDays((prev) =>
			prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
		)
	}

	async function handleSubmit(values: RangeValues) {
		if (selectedDays.length === 0) {
			return "Select at least one day of the week"
		}
		try {
			const result = await createRange.mutateAsync({
				...values,
				daysOfWeek: selectedDays,
			})
			toast.success(
				`Created ${result.count} availability slot${result.count !== 1 ? "s" : ""}`,
			)
			if (result.skipped > 0) {
				toast.warning(
					`${result.skipped} slot${result.skipped !== 1 ? "s" : ""} skipped — overlaps with existing availability (${result.skippedDates.join(", ")})`,
				)
			}
			onSuccess()
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className={"sm:max-w-lg"}>
				<DialogHeader>
					<DialogTitle>Add Availability</DialogTitle>
					<p className={"text-sm text-muted-foreground"}>
						Creates one slot per matching day in the selected range.
					</p>
				</DialogHeader>

				<Form<RangeValues>
					schema={rangeSchema}
					defaultValues={formDefaults}
					onSubmit={handleSubmit}
					className={"flex flex-col gap-4"}
				>
					<SelectFormField
						name={"practitionerId"}
						label={"Practitioner"}
						options={practitionerOptions}
						placeholder={"Select practitioner"}
					/>

					<div className={"grid grid-cols-2 gap-3"}>
						<InputFormField
							name={"startDate"}
							label={"From Date"}
							type={"date"}
						/>
						<InputFormField
							name={"endDate"}
							label={"To Date"}
							type={"date"}
						/>
					</div>

					{/* Days of week — separate state, outside form */}
					<div>
						<Label className={"mb-2 block text-sm font-medium"}>
							Days of Week
						</Label>
						<div className={"flex flex-wrap gap-2"}>
							{DAYS_OF_WEEK.map((d) => (
								<button
									key={d.value}
									type={"button"}
									onClick={() => toggleDay(d.value)}
									className={[
										"rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
										selectedDays.includes(d.value)
											? "border-primary bg-primary text-primary-foreground"
											: "border-input bg-background hover:bg-accent",
									].join(" ")}
								>
									{d.label}
								</button>
							))}
						</div>
					</div>

					<div className={"grid grid-cols-2 gap-3"}>
						<InputFormField
							name={"startTime"}
							label={"Start Time"}
							type={"time"}
						/>
						<InputFormField
							name={"endTime"}
							label={"End Time"}
							type={"time"}
						/>
					</div>

					<TimezoneFormField name={"timezone"} label={"Timezone"} />

					<TextareaFormField
						name={"notes"}
						label={"Notes (optional)"}
						placeholder={"Any notes for this availability block…"}
						rows={2}
					/>

					<DialogFooter>
						<Button type={"submit"} disabled={createRange.isPending}>
							{createRange.isPending && (
								<Loader2 className={"mr-2 size-4 animate-spin"} />
							)}
							Create Slots
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Book Appointment Dialog ────────────────────────────────────────────── */

function addMinutes(timeStr: string, minutes: number): string {
	const [h, m] = timeStr.split(":").map(Number)
	const total = h * 60 + m + minutes
	return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`
}

function BookAppointmentDialog({
	slot,
	patients,
	open,
	onOpenChange,
	onSuccess,
}: {
	slot: {
		availabilityId: string
		practitionerId: string
		startsAt: Date
		endsAt: Date
		timezone: string
		defaultAppointmentMinutes: number | null
	}
	patients: Patient[]
	open: boolean
	onOpenChange: (o: boolean) => void
	onSuccess: () => void
}) {
	const create = trpc.appointment.create.useMutation({
		onError: (e) => toast.error(e.message),
	})

	const patientOptions = patients.map((p) => ({
		value: p.id,
		label: `${p.firstName} ${p.lastName}`,
	}))

	const slotStart = utcToLocalParts(slot.startsAt, slot.timezone)
	const slotEnd = utcToLocalParts(slot.endsAt, slot.timezone)

	const windowLabel = new Intl.DateTimeFormat("en-CA", {
		timeZone: slot.timezone,
		weekday: "long",
		year: "numeric",
		month: "long",
		day: "numeric",
	}).format(slot.startsAt)

	const defaultEndTime = slot.defaultAppointmentMinutes
		? (() => {
				const candidate = addMinutes(slotStart.time, slot.defaultAppointmentMinutes)
				return candidate <= slotEnd.time ? candidate : slotEnd.time
			})()
		: slotEnd.time

	const formDefaults = useMemo<AppointmentValues>(
		() => ({
			patientId: "",
			type: "",
			status: "scheduled",
			reason: "",
			notes: "",
			startTime: slotStart.time,
			endTime: defaultEndTime,
		}),
		[slotStart.time, defaultEndTime],
	)

	async function handleSubmit(values: AppointmentValues) {
		try {
			const startsAt = clientLocalToUTC(slotStart.date, values.startTime, slot.timezone)
			const endsAt = clientLocalToUTC(slotStart.date, values.endTime, slot.timezone)
			await create.mutateAsync({
				practitionerId: slot.practitionerId,
				availabilityId: slot.availabilityId,
				patientId: values.patientId || null,
				startsAt,
				endsAt,
				timezone: slot.timezone,
				type: values.type || null,
				status: values.status,
				reason: values.reason || null,
				notes: values.notes || null,
			})
			toast.success("Appointment booked")
			onSuccess()
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className={"sm:max-w-md"}>
				<DialogHeader>
					<DialogTitle>Book Appointment</DialogTitle>
					<p className={"text-sm text-muted-foreground"}>{windowLabel}</p>
				</DialogHeader>

				<Form<AppointmentValues>
					schema={appointmentSchema}
					defaultValues={formDefaults}
					onSubmit={handleSubmit}
					className={"flex flex-col gap-4"}
				>
					{/* Time range within the availability window */}
					<div className={"rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground"}>
						Availability window: {slotStart.time} – {slotEnd.time} ({slot.timezone})
					</div>

					<div className={"grid grid-cols-2 gap-3"}>
						<InputFormField
							name={"startTime"}
							label={"Start Time"}
							type={"time"}
							min={slotStart.time}
							max={slotEnd.time}
						/>
						<InputFormField
							name={"endTime"}
							label={"End Time"}
							type={"time"}
							min={slotStart.time}
							max={slotEnd.time}
						/>
					</div>

					<SelectFormField
						name={"patientId"}
						label={"Patient"}
						options={patientOptions}
						placeholder={"Select patient"}
					/>
					<SelectFormField
						name={"type"}
						label={"Type"}
						options={APPOINTMENT_TYPES}
						placeholder={"Select type"}
					/>
					<SelectFormField
						name={"status"}
						label={"Status"}
						options={APPOINTMENT_STATUSES}
						placeholder={"Select status"}
					/>
					<InputFormField
						name={"reason"}
						label={"Reason for Visit"}
						placeholder={"Chief complaint or reason…"}
					/>
					<TextareaFormField
						name={"notes"}
						label={"Notes"}
						placeholder={"Additional notes…"}
						rows={2}
					/>

					<DialogFooter>
						<Button type={"submit"} disabled={create.isPending}>
							{create.isPending && (
								<Loader2 className={"mr-2 size-4 animate-spin"} />
							)}
							Book Appointment
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
